import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import * as _ from 'lodash';
import * as moment from 'moment';
import { Observable, of, Subject } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { LoginService } from '../../shared-modules/login/login.service';
import { UnsavedChangesGuard } from '../guards/unsaved-changes/unsaved-changes.guard';
import { UserSchoolYear } from '../user/user-school-year.model';
import { UserService } from '../user/user.service';
import { CheckForErrorsGuard } from './../guards/check-for-errors/check-for-errors.guard';
import { System } from './../System';
import { SchoolYearSettings } from './school-year-settings.model';
import { School } from './school.model';
import { Year } from './year.model';

@Injectable()
export class YearService {

    // This is the default course request year
    defaultCourseRequestYear: Year;

    // This is the default Versatrans year
    defaultTransportationYear: Year;

    // This is the default year
    loginYear: Year;

    // This is the previously selected year, which will always hold the default years last value
    private _previousYear: Year;
    get previousYear(): Year {
        return this._previousYear;
    }

    // This is the selected year, which is initialized to the default year
    private _defaultYear: Year;
    get defaultYear(): Year {
        return this._defaultYear;
    }
    set defaultYear(value: Year) {
        this._previousYear = this._defaultYear;
        this._defaultYear = value;
        this.observableDefaultYear.next(this._defaultYear);
    }

    private _student360Year: Year;
    get student360Year(): Year {
        return this._student360Year;
    }
    set student360Year(value: Year) {
        this._student360Year = value;
        this.observableStudent360Year.next(this._student360Year);
    }

    public observableDefaultYear = new Subject<Year>();
    observableDefaultYearStream$ = this.observableDefaultYear.asObservable();

    public observableStudent360Year = new Subject<Year>();
    observableStudent360YearStream$ = this.observableStudent360Year.asObservable();

    public allYears: Year[];
    private _years: Year[];

    public get years(): Year[] {
        return this._years;
    }
    public set years(value: Year[]) {
        this._years = value;
        this.observableYears.next(value);
    }

    private _studentYears: Year[];
    public get studentYears(): Year[] {
        return this._studentYears;
    }
    public set studentYears(value: Year[]) {
        this._studentYears = value;
    }

    private _yearsForC360: Year[];
    public get yearsForC360(): Year[] {
        return this._yearsForC360;
    }
    public set yearsForC360(value: Year[]) {
        this._yearsForC360 = value;
        this.observableYears.next(value);
    }

    private _schools: School[] = [];
    private _schoolsForC360: School[] = [];
    public get schools(): School[] {
        return this._schools;
    }
    public set schools(value: School[]) {
        if (
            value
            && value != this._schools
            && (
                !this._schools
                || value.length != this._schools.length
            )
        ) {

            this._schools = value;
            this.observableSchools.next(value);
        }
    }
    public get schoolsForC360(): School[] {
        return this._schoolsForC360;
    }
    public set schoolsForC360(value: School[]) {
        this._schoolsForC360 = value;
        this.observableSchools.next(value);
    }

    private observableYears = new Subject<Year[]>();
    observableYearStream$ = this.observableYears.asObservable();
    private observableSchools = new Subject<School[]>();
    observableSchoolsStream$ = this.observableSchools.asObservable();

    // This is used to Disabled School & Year dropdown based on Selected Tab
    observableSetSchoolDisabled = new Subject<boolean>();
    observableSetSchoolDisabledStream$ = this.observableSetSchoolDisabled.asObservable();
    observableSetYearDisabled = new Subject<boolean>();
    observableSetYearDisabledStream$ = this.observableSetYearDisabled.asObservable();

    private userSchools: School[];
    public allDistrictYears: Year[] = [];
    public allDistrictSchools: School[] = [];

    constructor(
        private http: HttpClient,
        private unsavedChangesGuard: UnsavedChangesGuard,
        private checkForErrorsGuard: CheckForErrorsGuard,
        private loginService: LoginService,
        private userService: UserService
    ) {
    }

    updateSelectedYear(newDefaultYear: Year) {
        const canContinue = this.unsavedChangesGuard.checkForDeactivation() && this.checkForErrorsGuard.checkForDeactivation();
        if (canContinue instanceof Observable) {
            canContinue.subscribe(clickedOk => {
                if (clickedOk) {
                    this.defaultYear = newDefaultYear;
                    if (this.loginService.LoginSettings.V10 || this.userService.userType != 1) {
                        this.years = this.allYears.filter(function (element) { return element.SchoolID == newDefaultYear.SchoolID; });
                    }
                    else {
                        this.schools = this.getSchoolsForSelectedYear(this.allYears, this.defaultYear);
                    }
                }
            });
        } else if (canContinue) {
            this.defaultYear = newDefaultYear;
            if (this.allYears.filter(x => x.SchoolID == 0).length > 0) {
                if (this.loginService.LoginSettings.V10 || this.userService.userType != 1) {
                    this.years = this.yearsForC360.filter(function (element) { return element.SchoolID == newDefaultYear.SchoolID; });
                }
                else {
                    this.schools = this.getSchoolsForSelectedYear(this.yearsForC360, this.defaultYear);
                }
            }
            else {
                if (this.loginService.LoginSettings.V10 || this.userService.userType != 1) {
                    this.years = this.allYears.filter(function (element) { return element.SchoolID == newDefaultYear.SchoolID; });
                }
                else {
                    this.schools = this.getSchoolsForSelectedYear(this.allYears, this.defaultYear);
                }
            }
            this.updateStudent360Year(this.defaultYear);
        }
    }

    updateStudent360Year(student360Year: Year) {
        const canContinue = this.unsavedChangesGuard.checkForDeactivation() && this.checkForErrorsGuard.checkForDeactivation();
        if (canContinue instanceof Observable) {
            canContinue.subscribe(clickedOk => {
                if (clickedOk) {
                    this.defaultYear = student360Year;
                    if (this.loginService.LoginSettings.V10 || this.userService.userType != 1) {
                        this.years = this.allYears.filter(function (element) { return element.SchoolID == student360Year.SchoolID; });
                    }
                    else {
                        this.schools = this.getSchoolsForSelectedYear(this.allYears, this.defaultYear);
                    }
                }
            });
        } else if (canContinue) {
            this.student360Year = student360Year;
            if (!this.loginService.LoginSettings.V10) {
                if (this.allYears.filter(x => x.SchoolID == 0).length > 0) {
                    if (this.loginService.LoginSettings.V10 || this.userService.userType != 1) {
                        this.years = this.yearsForC360.filter(function (element) { return element.SchoolID == student360Year.SchoolID; });
                        if (this.years.length == 0) {
                            this.years = this.allYears.filter(function (element) { return element.SchoolID == student360Year.SchoolID; });
                        }
                    }
                    else {
                        this.schools = this.getSchoolsForSelectedYear(this.allYears, this.defaultYear);
                    }
                }
                else {
                    if (this.loginService.LoginSettings.V10 || this.userService.userType != 1) {
                        this.years = this.allYears.filter(function (element) { return element.SchoolID == student360Year.SchoolID; });
                    }
                    else {
                        this.schools = this.getSchoolsForSelectedYear(this.allYears, this.defaultYear);
                    }
                }
            }

            if (this.student360Year.CourseRequestYear == true) {
                this.defaultCourseRequestYear = this.student360Year;
            }
            else {
                let courseRequestYears = this.years.filter(o => o.CourseRequestYear == true);
                if (courseRequestYears.length == 0) {
                    this.getDefaultCourseRequestYear().pipe(take(1)).subscribe();
                } else if (courseRequestYears.length == 1) {
                    this.defaultCourseRequestYear = courseRequestYears[0];
                }
                else if (courseRequestYears.length > 1) {
                    courseRequestYears = _.sortBy(courseRequestYears, ['EndDate'], ['DESC']);
                    this.defaultCourseRequestYear = courseRequestYears[0];
                }
            }
        }
    }

    updateSelectedSchool(schoolId: number) {
        const currentAY = this.defaultYear.AYIdentifier;
        let schoolYears = this.allYears.filter(function (element) { return element.SchoolID == schoolId; });
        if (schoolYears.length == 0) {
            schoolYears = this.yearsForC360.filter(function (element) { return element.SchoolID == schoolId; });
        }
        const newYear = schoolYears.filter(function (element) { return element.AYIdentifier == currentAY; })[0];
        if (newYear != undefined) {
            this.updateSelectedYear(newYear);
        }
        else {
            this.updateSelectedYear(schoolYears[0]);
        }
    }

    public isCurrentYear(year = this.defaultYear): boolean {
        return moment().isBetween(this.defaultYear.StartDate, this.defaultYear.EndDate, 'day', '[]');
    }

    getCurrentYear(): Year {
        for (let i = 0; i < this.years.length; i++) {
            if (this.isCurrentYear(this.years[i])) {
                return this.years[i];
            }
        }
        return null;
    }

    getUserYears(userId: number, usertype: number = 0, studentId: number = 0): Observable<Year[]> {
        const queryString: string = '?userId=' + userId +
            '&usertype=' + usertype +
            '&studentId=' + studentId;
        return this.http.get<Year[]>(System.apiURL.Student + 'GetUserYears' + queryString);
    }

    loadYears(years: Year[]) {
        this.allYears = years;
        const defaultCourseRequestYear = years.filter(y => y.CourseRequestYear)[0];
        if (!this.defaultCourseRequestYear) {
            if (defaultCourseRequestYear) {
                this.defaultCourseRequestYear = defaultCourseRequestYear;
            } else {
                this.getDefaultCourseRequestYear().pipe(take(1)).subscribe();
            }
        }
        this.setCurrentAttendanceDate(this.allYears);
        if (this.loginService.LoginSettings.V10) {
            this.schools = this.getSchoolsFromYears(years);
        }
        else {
            this.schools = this.getSchoolsForSelectedYear(years, this.defaultYear);
        }
    }

    loadYearsForC360(years: Year[]) {
        this.yearsForC360 = years;
        if (this.loginService.LoginSettings.V10) {
            this.schoolsForC360 = this.getSchoolsFromYears(this.yearsForC360);
        }
        else {
            this.schoolsForC360 = this.getSchoolsForSelectedYear(years, this.defaultYear);
        }
    }

    setCurrentAttendanceDate(years: Year[]) {
        // this should be set client-side rather than server-side to avoid time-zone mismatches which cause invalid dates
        years.forEach(year => {
            if (!year.CurrentAttendanceDate) {
                year.CurrentAttendanceDate = new Date();
            }
        });
    }

    getSchoolsFromYears(years: Year[]): School[] {
        const schools: School[] = [];
        for (let i = 0; i < years.length; i++) {
            if (schools.filter(function (element) { return element.Id == years[i].SchoolID; })[0] == undefined) {
                schools.push({ Id: years[i].SchoolID, SchoolName: years[i].SchoolName, SchoolCode: years[i].SchoolCode, SchoolDescription: '', SchoolType: years[i].SchoolType });
            }
        }
        return schools;
    }

    getSchoolsForSelectedYear(years: Year[], selectedYear: Year): School[] {
        const schools: School[] = [];
        const filteredYears = years.filter(o => o.Id == selectedYear.Id);
        filteredYears.forEach(obj => {
            if (schools.filter(o => o.Id == obj.SchoolID).length == 0) {
                schools.push({ Id: obj.SchoolID, SchoolName: obj.SchoolName, SchoolCode: obj.SchoolCode, SchoolDescription: '', SchoolType: obj.SchoolType });
            }
        });
        return schools;
    }

    getUserSchools(yearId: number, forceCheck = false): Observable<School[]> {
        // PRE:	yearId is the default user school year ID
        //  	forceCheck is true if you want to force the check to the server
        // POST: obtains the user schools

        // Checking the cache
        if (!forceCheck && this.userSchools) {
            return of(this.userSchools);
        }

        // Obtaining it from the server
        const queryString = '?yearId=' + yearId;
        return this.http.get<School[]>(System.apiURL.Student + 'GetUserSchools' + queryString).pipe(
            map(schools => {
                this.schools = schools;
                return this.schools;
            })
        );
    }

    getSchoolYearSettings(yearId: number, schoolId: number): Observable<SchoolYearSettings> {
        let queryString = '';
        queryString += '?yearId=' + yearId +
            '&schoolId=' + schoolId;
        return this.http.get<SchoolYearSettings>(System.apiURL.Student + 'GetSchoolYearSettings' + queryString);
    }

    getDefaultYear(userId: number): Observable<Year> {
        let queryString = '';
        queryString += '?userId=' + userId;
        return this.http.get<Year>(System.apiURL.Student + 'GetDefaultYear' + queryString);
    }

    getStudentSearchSchoolByYearId(yearId: number): Observable<UserSchoolYear[]> {
        // PRE: a year ID, probably the current one
        // POST: obtains the student search school using a supplied year ID
        const queryString = '?yearId=' + yearId;
        return this.http.get<UserSchoolYear[]>(System.apiURL.Student + 'GetStudentSearchSchoolByYearId' + queryString);
    }

    getAllSchoolYearsInDistrict(): Observable<Year[]> {
        return this.http.get<Year[]>(System.apiURL.Student + 'GetAllSchoolYearsInDistrict');
    }

    getDistrictYearsOnly(): Observable<Year[]> {
        return this.http.get<Year[]>(System.apiURL.Student + 'GetDistrictYearsOnly');
    }
    getDistrictYearbyIdentifier(): Observable<Year> {
        const queryString = '?ayIdentifier=' + this.defaultYear.AYIdentifier;
        return this.http.get<Year>(System.apiURL.Student + 'GetDistrictYearbyIdentifier' + queryString);
    }
    getAllSchools(): Observable<School[]> {
        return this.http.get<School[]>(System.apiURL.Student + 'GetAllSchools');
    }

    getAllEnrolledYears(studentId: number, schoolId: number): Observable<Year[]> {
        const queryString = '?studentId=' + studentId + '&schoolId=' + schoolId;
        return this.http.get<Year[]>(System.apiURL.Student + 'GetAllEnrolledYears' + queryString);
    }

    getSchoolsBySchoolIds(schoolIds: number[]): Observable<School[]> {
        return this.http.post<School[]>(System.apiURL.Student + 'GetSchoolsBySchoolIds', schoolIds);
    }

    getSchoolsStudentIds(schoolIds: number[], yearId: number): Observable<School[]> {
        const queryString = '?yearId=' + yearId;
        return this.http.post<School[]>(System.apiURL.Student + 'GetSchoolsStudentIds' + queryString, schoolIds);
    }

    getDefaultCourseRequestYear(): Observable<Year> {
        return this.http.get<Year>(System.apiURL.Student + 'GetDefaultCourseRequestYear').pipe(
            map(result => {
                this.defaultCourseRequestYear = result;
                return result;
            })
        );
    }

    isDateWithinCurrentYear(testDate: Date | string | moment.Moment): boolean {
        return moment(testDate) >= moment(this.defaultYear.StartDate) && moment(testDate) <= moment(this.defaultYear.EndDate);
    }

    isDateWithinGivenYear(testDate: Date | string | moment.Moment, year: Year): boolean {
        return moment(testDate) >= moment(year.StartDate) && moment(testDate) <= moment(year.EndDate);
    }
}
